import React, { useState, useEffect } from 'react';
import { AreaChart, Area, ComposedChart, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { getPurchases } from '../services/firestoreService';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import BBALoader from '../components/BBALoader';
import { PinnableCard, ReportBackdrop } from '../components/crm/reportCards';
import {
  GLASS_PALETTE,
  GLASS_TONE,
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassCardHeader,
  GlassFigure,
  GlassChip,
  GlassTooltip,
  GlassEmpty,
  GlassDonut,
  SlidingPills,
  glassChartTheme,
} from '../components/glass/GlassUi';

const TIME_OPTIONS = [
  { value: 'All Time', label: 'All Time' },
  { value: 'This Year', label: 'This Year' },
  { value: 'This Quarter', label: 'This Quarter' },
  { value: 'This Month', label: 'This Month' },
];

const PERIOD_PHRASE = { 'All Time': 'all time', 'This Year': 'this year', 'This Quarter': 'this quarter', 'This Month': 'this month' };

const formatDollars = (value) => `$${Number(value || 0).toLocaleString()}`;

// GlassTooltip with per-series value formatting and an optional label formatter
const FormattedTooltip = ({ formatValue, formatLabel, payload, label, ...rest }) => (
  <GlassTooltip
    {...rest}
    label={formatLabel ? formatLabel(label) : label}
    payload={(payload || []).map(p => ({ ...p, value: formatValue ? formatValue(p.value, p.name) : p.value }))}
  />
);

// Two-line empty state inside a chart area
const ChartEmpty = ({ title, hint }) => (
  <GlassEmpty className="h-full flex-col gap-1">
    <p className="text-base font-medium text-gray-600 dark:text-white/60">{title}</p>
    <p className="text-sm">{hint}</p>
  </GlassEmpty>
);

// Convert Firestore Timestamps, ISO strings, or Dates to a JS Date
const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (value.seconds) return new Date(value.seconds * 1000);
  return null;
};

// When the purchase actually happened: Programs use startDate, A La Carte uses date
const getPurchaseDate = (purchase) => purchase.startDate || purchase.date || purchase.createdAt;

// When a lead entered the pipeline (persists after conversion to client)
const getLeadReceivedDate = (account) => account.receivedDate || account.createdAt;

// When an account became a client: explicit timestamps, then the
// "Converted to Client" status history entry, then contract start
const getClientSignedDate = (account) => {
  if (account.wonAt) return account.wonAt;
  if (account.convertedAt) return account.convertedAt;
  const conversion = (account.statusHistory || []).find(
    entry => typeof entry.action === 'string' && entry.action.startsWith('Converted to Client')
  );
  const conversionDate = conversion ? toDate(conversion.date) : null;
  return conversionDate || account.contractStart || account.createdAt;
};

const isWon = (account) =>
  account.type === 'Clients' || String(account.result || '').toLowerCase() === 'won';

const isLost = (account) => String(account.result || '').toLowerCase() === 'lost';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const SalesReports = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [purchases, setPurchases] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [timeFilter, setTimeFilter] = useState('This Year');

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch purchases data
        const purchasesData = await getPurchases();
        setPurchases(purchasesData);

        // Fetch leads AND clients — converted leads change type to 'Clients',
        // so both are needed for close ratio and new-client counts.
        // No orderBy: it would silently exclude docs missing createdAt.
        const accountsQuery = query(
          collection(db, 'accounts'),
          where('type', 'in', ['Leads', 'Clients'])
        );
        const accountsSnapshot = await getDocs(accountsQuery);
        const accountsData = accountsSnapshot.docs.map(docSnap => {
          const d = docSnap.data();
          return {
            id: docSnap.id,
            ...d,
            createdAt: toDate(d.createdAt),
            receivedDate: toDate(d.receivedDate),
            lastActivityAt: toDate(d.lastActivityAt),
            lostAt: toDate(d.lostAt),
            wonAt: toDate(d.wonAt),
            convertedAt: toDate(d.convertedAt),
            contractStart: toDate(d.contractStart)
          };
        });
        setAccounts(accountsData);

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Leads still in the pipeline vs. accounts that became clients
  const clients = accounts.filter(a => a.type === 'Clients');
  const pipelineLeads = accounts.filter(a => a.type === 'Leads');

  // Revenue-counting purchases: skip drafts and cancellations
  const countedPurchases = purchases.filter(p => p.status !== 'Cancelled' && p.status !== 'Draft');

  // Start of the selected time window (null = All Time, no lower bound)
  const getWindowStart = () => {
    if (timeFilter === 'All Time') return null;
    const now = new Date();
    const start = new Date();
    if (timeFilter === 'This Year') {
      start.setMonth(0, 1);
    } else if (timeFilter === 'This Quarter') {
      start.setMonth(Math.floor(now.getMonth() / 3) * 3, 1);
    } else if (timeFilter === 'This Month') {
      start.setDate(1);
    }
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const inWindow = (date) => {
    const windowStart = getWindowStart();
    return windowStart ? (date && date >= windowStart) : true;
  };

  // Chart buckets for the selected time filter. For All Time the year range
  // comes from the dates actually present in the data.
  const getPeriodBuckets = (datesForAllTime) => {
    const now = new Date();
    const year = now.getFullYear();

    if (timeFilter === 'This Quarter') {
      const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
      return [0, 1, 2].map(i => {
        const m = quarterStartMonth + i;
        return {
          period: MONTH_NAMES[m],
          start: new Date(year, m, 1),
          end: new Date(year, m + 1, 0, 23, 59, 59, 999)
        };
      });
    }

    if (timeFilter === 'This Month') {
      const month = now.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const weeks = Math.ceil(daysInMonth / 7);
      return Array.from({ length: weeks }, (_, i) => ({
        period: `Week ${i + 1}`,
        start: new Date(year, month, i * 7 + 1),
        end: new Date(year, month, Math.min((i + 1) * 7, daysInMonth), 23, 59, 59, 999)
      }));
    }

    if (timeFilter === 'All Time') {
      const years = [...new Set(
        (datesForAllTime || []).filter(Boolean).map(d => d.getFullYear())
      )].sort();
      return years.map(y => ({
        period: String(y),
        start: new Date(y, 0, 1),
        end: new Date(y, 11, 31, 23, 59, 59, 999)
      }));
    }

    // This Year — Jan through Dec
    return Array.from({ length: 12 }, (_, m) => ({
      period: MONTH_NAMES[m],
      start: new Date(year, m, 1),
      end: new Date(year, m + 1, 0, 23, 59, 59, 999)
    }));
  };

  const inBucket = (date, bucket) => date && date >= bucket.start && date <= bucket.end;

  // Revenue + new clients signed per period
  const calculateRevenueData = () => {
    const buckets = getPeriodBuckets([
      ...countedPurchases.map(getPurchaseDate),
      ...clients.map(getClientSignedDate)
    ]);

    return buckets.map(bucket => {
      const bucketPurchases = countedPurchases.filter(p => inBucket(getPurchaseDate(p), bucket));
      return {
        period: bucket.period,
        revenue: bucketPurchases.reduce((sum, p) => sum + (p.total || 0), 0),
        deals: bucketPurchases.length,
        newClients: clients.filter(c => inBucket(getClientSignedDate(c), bucket)).length
      };
    });
  };

  const calculateSalesByProgram = () => {
    const filteredPurchases = countedPurchases.filter(p => inWindow(getPurchaseDate(p)));
    const programPurchases = filteredPurchases.filter(p => p.type === 'Programs' && p.program);
    const programCounts = {};
    const programRevenue = {};
    
    programPurchases.forEach(purchase => {
      const program = purchase.program;
      programCounts[program] = (programCounts[program] || 0) + 1;
      programRevenue[program] = (programRevenue[program] || 0) + (purchase.total || 0);
    });
    
    const totalPrograms = Object.keys(programCounts).length;
    const colors = GLASS_PALETTE;
    
    return Object.keys(programCounts).map((program, index) => ({
      name: program,
      value: totalPrograms > 0 ? Math.round((programCounts[program] / programPurchases.length) * 100) : 0,
      revenue: programRevenue[program] || 0,
      color: colors[index % colors.length]
    }));
  };

  const calculateLeadStageDuration = () => {
    // Open pipeline leads only — won leads become Clients, lost leads have result 'Lost'
    const filteredLeads = pipelineLeads.filter(l => !l.result && inWindow(getLeadReceivedDate(l)));
    const stageDurations = {};
    const stageCounts = {};

    // Actual pipeline stage order from the Accounts kanban
    const stageOrder = ['Prospects', 'Lead Received', 'Discovery Call', 'Assessment', 'Onboarding Call', 'Proposal'];

    filteredLeads.forEach(lead => {
      if (lead.stage && stageOrder.includes(lead.stage)) {
        const stage = lead.stage;
        if (!stageDurations[stage]) {
          stageDurations[stage] = [];
          stageCounts[stage] = 0;
        }
        
        // Calculate days since creation (simplified - in real implementation you'd track stage changes)
        if (lead.createdAt) {
          const daysSinceCreation = Math.floor((new Date() - lead.createdAt) / (1000 * 60 * 60 * 24));
          stageDurations[stage].push(daysSinceCreation);
          stageCounts[stage]++;
        }
      }
    });
    
    return stageOrder
      .filter(stage => stageCounts[stage] > 0)
      .map(stage => ({
        stage,
        avgDays: Math.round(
          stageDurations[stage].reduce((sum, days) => sum + days, 0) / stageDurations[stage].length
        )
      }));
  };

  // Close ratio per period: of the leads received in that period (including
  // ones that have since converted to clients), how many were won
  const calculateCloseRatio = () => {
    const buckets = getPeriodBuckets(accounts.map(getLeadReceivedDate));

    return buckets.map(bucket => {
      const cohort = accounts.filter(a => inBucket(getLeadReceivedDate(a), bucket));
      const wonCount = cohort.filter(isWon).length;
      const closeRatio = cohort.length > 0 ? (wonCount / cohort.length) * 100 : 0;
      return {
        period: bucket.period,
        deals: cohort.length,
        closeRatio: Math.round(closeRatio * 10) / 10
      };
    });
  };

  const calculateLostLeads = () => {
    const lostLeads = accounts.filter(a =>
      isLost(a) && inWindow(a.lostAt || getLeadReceivedDate(a))
    );

    const reasonCounts = {};
    lostLeads.forEach(lead => {
      const reason = lead.resultReason || lead.substatus || lead.reason || lead.lostReason || 'Unknown Reason';
      reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
    });

    const totalLost = lostLeads.length;

    return Object.keys(reasonCounts).map(reason => ({
      reason,
      count: reasonCounts[reason],
      percentage: totalLost > 0 ? Math.round((reasonCounts[reason] / totalLost) * 100 * 10) / 10 : 0
    }));
  };

  // Get calculated data
  const monthlyRevenueData = calculateRevenueData();
  const salesByProduct = calculateSalesByProgram();
  const leadStageDuration = calculateLeadStageDuration();
  const closeRatioData = calculateCloseRatio();
  const lostLeadsData = calculateLostLeads();


  if (loading) {
    return (
      <GlassPage backdrop={<ReportBackdrop />}>
        <div className="flex h-64 items-center justify-center"><BBALoader size={96} /></div>
      </GlassPage>
    );
  }

  const chart = glassChartTheme(darkMode);
  const totalRevenue = monthlyRevenueData.reduce((sum, p) => sum + p.revenue, 0);
  const totalNewClients = monthlyRevenueData.reduce((sum, p) => sum + p.newClients, 0);
  const programRevenue = salesByProduct.reduce((sum, p) => sum + p.revenue, 0);

  return (
    <GlassPage backdrop={<ReportBackdrop />}>
      <GlassPageHeader
        onBack={() => navigate('/crm/reports')}
        backTitle="Back to reports"
        title={<>Sales<br />Reports</>}
        controls={(
          <SlidingPills size="sm" options={TIME_OPTIONS} value={timeFilter} onChange={setTimeFilter} ariaLabel="Timeframe" />
        )}
      />

      {/* Charts replay their rise whenever the timeframe changes */}
      <div key={timeFilter} className="space-y-4">
        {/* Revenue Section */}
        <PinnableCard id="revenue" title="Revenue & New Clients" size="wide">
        <GlassCard className="p-5" delay={0}>
          <GlassCardHeader title="Revenue & New Clients" />
          {monthlyRevenueData.length > 0 && (
            <>
              <GlassFigure className="mt-4" value={formatDollars(totalRevenue)} caption={`revenue, ${PERIOD_PHRASE[timeFilter] || ''}`} />
              <div className="mt-4 flex flex-wrap gap-2">
                <GlassChip color={GLASS_TONE.mint} label="Revenue" value={formatDollars(totalRevenue)} />
                <GlassChip color={GLASS_TONE.blue} label="New Clients" value={totalNewClients} />
              </div>
            </>
          )}
          <div className="mt-4 h-72">
            {monthlyRevenueData.length === 0 ? (
              <ChartEmpty title="No revenue recorded" hint="Add client purchases to see revenue data" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={monthlyRevenueData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="salesRevenueFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={GLASS_TONE.mint} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={GLASS_TONE.mint} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke={chart.grid} strokeDasharray="4 6" />
                  <XAxis dataKey="period" tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={6} />
                  <YAxis yAxisId="revenue" tickFormatter={(value) => `$${value.toLocaleString()}`} tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} width={72} />
                  <YAxis yAxisId="clients" orientation="right" allowDecimals={false} tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} width={32} />
                  <Tooltip
                    cursor={{ fill: chart.cursor }}
                    content={<FormattedTooltip formatValue={(value, name) => (name === 'Revenue' ? formatDollars(value) : value)} />}
                  />
                  <Bar yAxisId="clients" dataKey="newClients" name="New Clients" fill={GLASS_TONE.blue} maxBarSize={28} radius={[8, 8, 8, 8]} background={{ fill: chart.track, radius: 8 }} animationDuration={700} />
                  <Area yAxisId="revenue" type="monotone" dataKey="revenue" name="Revenue" stroke={GLASS_TONE.mint} strokeWidth={2.5} fill="url(#salesRevenueFill)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} animationDuration={800} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </GlassCard>
        </PinnableCard>

        {/* Sales Distribution */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <PinnableCard id="sales-by-program" title="Sales by Program">
          <GlassCard className="flex flex-col p-5" delay={80}>
            <GlassCardHeader title="Sales by Program" />
            {salesByProduct.length === 0 ? (
              <div className="mt-4 h-80">
                <ChartEmpty title="No sales recorded" hint="Add client purchases to see breakdown" />
              </div>
            ) : (
              <>
                <div className="mt-4 h-64">
                  <GlassDonut data={salesByProduct} dataKey="value" nameKey="name" inner={96} outer={108} darkMode={darkMode} unit="%">
                    <div className="text-3xl font-medium text-gray-900 tabular-nums dark:text-white">{formatDollars(programRevenue)}</div>
                    <div className="text-xs text-gray-500 dark:text-white/[0.45]">program revenue</div>
                  </GlassDonut>
                </div>
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  {salesByProduct.map((entry) => (
                    <GlassChip key={entry.name} color={entry.color} label={entry.name} value={`${entry.value}%`} />
                  ))}
                </div>
              </>
            )}
          </GlassCard>
          </PinnableCard>

          <PinnableCard id="lead-stage-duration" title="Lead Stage Duration">
          <GlassCard className="p-5" delay={140}>
            <GlassCardHeader title="Lead Stage Duration" />
            <div className="mt-4 h-[420px]">
              {leadStageDuration.length === 0 ? (
                <ChartEmpty title="No leads recorded" hint="Add leads to see stage duration data" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={leadStageDuration} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }} barCategoryGap="24%">
                    <CartesianGrid horizontal={false} stroke={chart.grid} strokeDasharray="4 6" />
                    <XAxis type="number" allowDecimals={false} tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis dataKey="stage" type="category" width={120} tick={{ fill: chart.tick, fontSize: 12 }} axisLine={false} tickLine={false} />
                    <Tooltip
                      cursor={{ fill: chart.cursor }}
                      content={<FormattedTooltip formatLabel={(label) => `${label} Stage`} formatValue={(value) => `${value} days`} />}
                    />
                    <Bar dataKey="avgDays" name="Average Days" radius={[0, 12, 12, 0]} maxBarSize={30} background={{ fill: chart.track, radius: 12 }} animationDuration={700}>
                      {leadStageDuration.map((entry, idx) => (
                        <Cell key={`s-${idx}`} fill={GLASS_PALETTE[(idx + 1) % GLASS_PALETTE.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </GlassCard>
          </PinnableCard>
        </div>

        {/* Close Ratio and Lost Leads */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <PinnableCard id="close-ratio" title="Close Ratio">
          <GlassCard className="p-5" delay={200}>
            <GlassCardHeader
              title="Close Ratio"
              right={closeRatioData.length > 0 ? <GlassChip color={GLASS_TONE.mint} label="Close Ratio" /> : null}
            />
            <div className="mt-4 h-80">
              {closeRatioData.length === 0 ? (
                <ChartEmpty title="No leads recorded" hint="Add leads to see close ratio data" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={closeRatioData} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="salesCloseFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={GLASS_TONE.mint} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={GLASS_TONE.mint} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke={chart.grid} strokeDasharray="4 6" />
                    <XAxis dataKey="period" tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={6} />
                    <YAxis tickFormatter={(v) => `${v}%`} tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} width={44} />
                    <Tooltip cursor={{ stroke: chart.grid }} content={<FormattedTooltip formatValue={(value) => `${value}%`} />} />
                    <Area type="monotone" dataKey="closeRatio" name="Close Ratio" stroke={GLASS_TONE.mint} strokeWidth={2.5} fill="url(#salesCloseFill)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} animationDuration={800} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </GlassCard>
          </PinnableCard>

          <PinnableCard id="lost-leads" title="Lost Leads">
          <GlassCard className="p-5" delay={260}>
            <GlassCardHeader title="Lost Leads" />
            <div className="mt-4 h-80">
              {lostLeadsData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={lostLeadsData} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }} barCategoryGap="24%">
                    <CartesianGrid horizontal={false} stroke={chart.grid} strokeDasharray="4 6" />
                    <XAxis type="number" allowDecimals={false} tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis dataKey="reason" type="category" width={150} tick={{ fill: chart.tick, fontSize: 12 }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: chart.cursor }} content={<GlassTooltip unit="leads" />} />
                    <Bar dataKey="count" name="Count" fill={GLASS_TONE.red} radius={[0, 12, 12, 0]} maxBarSize={30} background={{ fill: chart.track, radius: 12 }} animationDuration={700} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ChartEmpty title="No Lost Leads" hint="No leads have been marked as lost yet" />
              )}
            </div>
          </GlassCard>
          </PinnableCard>
        </div>
      </div>
    </GlassPage>
  );
};

export default SalesReports;
